import { getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { matchDoc, matchesCol } from './matches';
import { tournamentDoc } from './tournaments';
import type { Match } from './types';

/** Walkover/bye-матч — авто-каскад, не сыгран руками пользователя. */
function isAutoWalkover(m: Match): boolean {
  return (
    m.status === 'COMPLETED' &&
    !!m.score &&
    (m.score.summary === 'walkover' || m.score.summary === 'bye')
  );
}

/**
 * Убирает игрока из указанного слота целевого матча. Если матч был
 * авто-walkover — откатывает и его, рекурсивно вычищая продвинутого им игрока
 * дальше по цепочке. Мутирует матчи в `byId`, копит id в `changed`.
 */
function removeAndCascade(
  byId: Map<string, Match>,
  targetId: string,
  playerId: string,
  changed: Set<string>
): void {
  const m = byId.get(targetId);
  if (!m) return;

  // Если это авто-walkover — нужно откатить его до вычистки слотов,
  // чтобы знать, кого он успел продвинуть дальше.
  const wasAutoWalkover = isAutoWalkover(m);
  const advancedId = wasAutoWalkover && m.score ? m.score.winnerId : null;

  // Вычищаем игрока из его слота.
  if (m.playerAId === playerId) {
    m.playerAId = null;
    m.playerAName = null;
  } else if (m.playerBId === playerId) {
    m.playerBId = null;
    m.playerBName = null;
  }

  if (wasAutoWalkover) {
    // Откатываем сам walkover-матч.
    m.score = undefined;
    m.status = m.playerAId && m.playerBId ? 'READY' : 'PENDING';
    changed.add(m.id);

    // Walkover продвинул победителя дальше по nextMatchId — вычищаем его.
    if (advancedId && m.nextMatchId) {
      removeAndCascade(byId, m.nextMatchId, advancedId, changed);
    }
    return;
  }

  // Обычный матч: игрок убран, статус мог понизиться.
  if (m.status !== 'COMPLETED') {
    m.status = m.playerAId && m.playerBId ? 'READY' : 'PENDING';
  }
  changed.add(m.id);
}

/**
 * Проверяет, есть ли по цепочке продвижения матч, сыгранный руками
 * пользователя (COMPLETED и не walkover/bye). Такой матч блокирует откат.
 */
function chainHasManualMatch(
  byId: Map<string, Match>,
  targetId: string | undefined,
  visited: Set<string>
): boolean {
  if (!targetId || visited.has(targetId)) return false;
  visited.add(targetId);
  const m = byId.get(targetId);
  if (!m) return false;

  if (m.status === 'COMPLETED' && !isAutoWalkover(m)) {
    return true;
  }

  if (isAutoWalkover(m) && m.nextMatchId) {
    // Авто-walkover продвинул победителя дальше — проверяем его цепочку.
    return chainHasManualMatch(byId, m.nextMatchId, visited);
  }
  return false;
}

export async function undoMatchResult(tournamentId: string, matchId: string): Promise<void> {
  // Список всех матчей читаем вне транзакции, чтобы получить id.
  const allSnap = await getDocs(matchesCol(tournamentId));
  const allIds = allSnap.docs.map((d) => d.id);

  await runTransaction(db, async (tx) => {
    // --- Фаза чтения: все матчи турнира + турнир в память ---
    const byId = new Map<string, Match>();
    for (const id of allIds) {
      const snap = await tx.get(matchDoc(tournamentId, id));
      if (snap.exists()) byId.set(id, { ...snap.data() });
    }
    const tournamentRef = tournamentDoc(tournamentId);
    const tournamentSnap = await tx.get(tournamentRef);

    const match = byId.get(matchId);
    if (!match) throw new Error('Матч не найден');
    if (match.status !== 'COMPLETED' || !match.score) {
      throw new Error('Матч не сыгран — откатывать нечего');
    }

    const winnerId = match.score.winnerId;
    const loserId = winnerId === match.playerAId ? match.playerBId : match.playerAId;

    // --- Проверка: не сыгран ли руками кто-то ниже по цепочке ---
    if (chainHasManualMatch(byId, match.nextMatchId, new Set())) {
      throw new Error('Следующий матч уже сыгран — сначала откатите его');
    }
    if (
      loserId &&
      chainHasManualMatch(byId, match.loserNextMatchId, new Set())
    ) {
      throw new Error('Матч проигравшего уже сыгран — сначала откатите его');
    }

    // --- Откат в памяти ---
    const changed = new Set<string>();
    match.score = undefined;
    match.status = match.playerAId && match.playerBId ? 'READY' : 'PENDING';
    changed.add(match.id);

    // Вычищаем победителя из next-цепочки.
    if (match.nextMatchId) {
      removeAndCascade(byId, match.nextMatchId, winnerId, changed);
    }
    // Вычищаем проигравшего из loser-цепочки (Double Elimination).
    if (match.loserNextMatchId && loserId) {
      removeAndCascade(byId, match.loserNextMatchId, loserId, changed);
    }

    // --- Фаза записи ---
    for (const id of changed) {
      const m = byId.get(id)!;
      tx.update(matchDoc(tournamentId, id), {
        playerAId: m.playerAId,
        playerBId: m.playerBId,
        playerAName: m.playerAName,
        playerBName: m.playerBName,
        status: m.status,
        score: m.score ?? null,
      });
    }

    // Турнир: вернуть IN_PROGRESS только если он сейчас COMPLETED.
    if (tournamentSnap.exists() && tournamentSnap.data().status === 'COMPLETED') {
      tx.update(tournamentRef, {
        status: 'IN_PROGRESS',
        updatedAt: serverTimestamp(),
      });
    }
  });
}
